// ══════════════════════════════════════════════════════
//  自动发现项目网站 / GitHub / Twitter
//  不依赖 Claude 编造 URL，通过多源探测找到真实链接
// ══════════════════════════════════════════════════════

/**
 * 从项目名生成可能的域名候选
 * "Forecast Protocol" → [forecastprotocol, forecast-protocol, forecast]
 */
function nameToSlugs(name) {
  const lower = (name || "").toLowerCase().trim();
  const noSpace = lower.replace(/\s+/g, "");
  const dashSpace = lower.replace(/\s+/g, "-");
  const firstWord = lower.split(/\s+/)[0];
  const noSuffix = noSpace.replace(/(protocol|network|finance|labs|app|io|xyz)$/, "");
  return [...new Set([noSpace, dashSpace, firstWord, noSuffix].filter(s => s && s.length >= 3))];
}

/**
 * 1. GitHub 搜索：找 star 最多、名字最匹配的仓库
 * 返回 homepage 字段（仓库主页 URL）
 */
export async function discoverViaGitHub(projectName) {
  if (!projectName) return null;
  try {
    const q = encodeURIComponent(`${projectName} in:name,description topic:crypto`);
    const url = `https://api.github.com/search/repositories?q=${q}&sort=stars&order=desc&per_page=5`;
    const res = await fetch(url, { signal: AbortSignal.timeout(8000) });
    if (!res.ok) return null;
    const data = await res.json();
    const items = data.items || [];
    if (!items.length) return null;

    // 名字归一化用于匹配
    const norm = s => (s || "").toLowerCase().replace(/[^a-z0-9]/g, "");
    const qNorm = norm(projectName);

    // 找名字最匹配的仓库（优先名称包含项目名）
    const match = items.find(r => norm(r.name).includes(qNorm) || norm(r.full_name).includes(qNorm))
               || items[0];

    return {
      source: "github",
      website: match.homepage || "",
      github: match.html_url || "",
      stars: match.stargazers_count,
      description: match.description || "",
      score: match.stargazers_count > 100 ? "high" : match.stargazers_count > 10 ? "medium" : "low",
    };
  } catch { return null; }
}

/**
 * 2. DeFiLlama 协议匹配：拿到官方 URL + Twitter
 */
export async function discoverViaDefiLlama(projectName) {
  if (!projectName) return null;
  try {
    const protocols = await fetch("https://api.llama.fi/protocols", { signal: AbortSignal.timeout(8000) })
      .then(r => r.json())
      .catch(() => []);
    const norm = s => (s || "").toLowerCase().replace(/[^a-z0-9]/g, "");
    const qNorm = norm(projectName);
    const match = protocols.find(p => norm(p.name) === qNorm)
              || protocols.find(p => norm(p.name).includes(qNorm) && qNorm.length >= 5);
    if (!match) return null;
    return {
      source: "defillama",
      website: match.url || "",
      twitter: match.twitter ? `https://twitter.com/${match.twitter}` : "",
      logo: match.logo || "",
      score: "high",
    };
  } catch { return null; }
}

/**
 * 3. 域名模式 + Wayback 验证：试常见 TLD，用 Wayback 快照确认真实存在
 * 走我们的代理避免 CORS
 */
export async function discoverViaDomainPattern(projectName, proxyBase = "") {
  if (!projectName) return null;
  const slugs = nameToSlugs(projectName);
  const tlds = ["xyz", "io", "com", "fi", "finance", "network", "app", "protocol"];

  // 生成所有候选 domain（限制最多 20 个避免 API 压力）
  const candidates = [];
  for (const slug of slugs) {
    for (const tld of tlds) {
      candidates.push(`${slug}.${tld}`);
      if (candidates.length >= 20) break;
    }
    if (candidates.length >= 20) break;
  }

  // 并行查询 Wayback（已有代理 /wb-snap）
  const results = await Promise.all(
    candidates.map(async domain => {
      try {
        const url = `${proxyBase}/wb-snap?q=${encodeURIComponent(domain)}`;
        const res = await fetch(url, { signal: AbortSignal.timeout(6000) });
        if (!res.ok) return null;
        const data = await res.json();
        if (!Array.isArray(data) || data.length < 2) return null;
        const ts = data[1][0];
        if (!ts || ts.length < 8) return null;
        return { domain, ts, snapshotDate: `${ts.slice(0,4)}-${ts.slice(4,6)}-${ts.slice(6,8)}` };
      } catch { return null; }
    })
  );

  const valid = results.filter(Boolean);
  if (!valid.length) return null;

  // 优先选 Wayback 最早的（最老的域名最可能是真项目）
  valid.sort((a, b) => a.ts.localeCompare(b.ts));
  return {
    source: "domain-pattern",
    website: `https://${valid[0].domain}`,
    waybackEarliest: valid[0].snapshotDate,
    score: valid.length >= 2 ? "medium" : "low",  // 多个域名命中说明分数低（可能撞名）
  };
}

/**
 * 4. Brave Search 网络搜索 - 找官方网站
 * 排除社交/聚合/百科等噪音域名，优先返回名字匹配的域名
 */
const SEARCH_DOMAIN_BLOCKLIST = [
  "twitter.com", "x.com", "facebook.com", "linkedin.com", "instagram.com",
  "youtube.com", "tiktok.com", "discord.com", "telegram.com", "reddit.com",
  "github.com", "gitlab.com", "medium.com", "substack.com", "mirror.xyz",
  "wikipedia.org", "investopedia.com", "google.com", "bing.com",
  "coingecko.com", "coinmarketcap.com", "rootdata.com", "defillama.com",
  "messari.io", "cryptorank.io", "icodrops.com",
  "cointelegraph.com", "theblock.co", "decrypt.co", "coindesk.com", "bloomberg.com",
];

function isBlockedDomain(urlStr) {
  try {
    const domain = new URL(urlStr).hostname.replace(/^www\./, '').toLowerCase();
    return SEARCH_DOMAIN_BLOCKLIST.some(b => domain === b || domain.endsWith("." + b));
  } catch { return true; }
}

export async function discoverViaBraveSearch(projectName, proxyBase = "") {
  if (!projectName) return null;
  try {
    const q = `${projectName} crypto web3 official site`;
    const url = `${proxyBase}/api/brave-search?q=${encodeURIComponent(q)}&count=10`;
    const res = await fetch(url, { signal: AbortSignal.timeout(10000) });
    if (!res.ok) return null;
    const data = await res.json();
    const results = data.web?.results || [];

    const norm = s => (s || "").toLowerCase().replace(/[^a-z0-9]/g, "");
    const qNorm = norm(projectName);

    // 过滤掉黑名单域名
    const candidates = results.filter(r => r.url && !isBlockedDomain(r.url));
    if (!candidates.length) return null;

    // 优先：域名包含项目名的
    const exactDomain = candidates.find(r => {
      try {
        const domain = new URL(r.url).hostname.replace(/^www\./, '');
        const slug = domain.split('.')[0];
        return norm(slug) === qNorm || norm(domain.replace(/\..+$/, '')) === qNorm;
      } catch { return false; }
    });
    const partialDomain = candidates.find(r => {
      try {
        const domain = new URL(r.url).hostname.replace(/^www\./, '');
        return norm(domain).includes(qNorm) && qNorm.length >= 5;
      } catch { return false; }
    });

    const best = exactDomain || partialDomain || candidates[0];
    if (!best) return null;

    try {
      const u = new URL(best.url);
      return {
        source: "brave-search",
        website: `https://${u.hostname}`,
        title: best.title,
        description: best.description,
        score: exactDomain ? "high" : partialDomain ? "medium" : "low",
      };
    } catch { return null; }
  } catch { return null; }
}

/**
 * 5. Twitter Bio 解析 - 通过 Brave Search 找到 Twitter 页面并提取 bio 中的 URL
 * 输入：Twitter handle 或完整 X URL
 */
export async function discoverViaTwitterBio(twitterRef, proxyBase = "") {
  if (!twitterRef) return null;
  // 归一化 handle: 移除 @, 移除 URL 前缀
  const handle = String(twitterRef)
    .replace(/^@/, '')
    .replace(/^https?:\/\/(twitter\.com|x\.com)\//i, '')
    .replace(/\/$/, '')
    .split(/[/?]/)[0];
  if (!handle || handle.length < 2) return null;

  try {
    // 通过 Brave Search 查找该 Twitter 页面（snippet 通常包含 bio）
    const q = `"twitter.com/${handle}" OR "x.com/${handle}"`;
    const url = `${proxyBase}/api/brave-search?q=${encodeURIComponent(q)}&count=5`;
    const res = await fetch(url, { signal: AbortSignal.timeout(10000) });
    if (!res.ok) return null;
    const data = await res.json();
    const results = data.web?.results || [];

    // 找匹配的 twitter/x 页面
    const tw = results.find(r => {
      const u = (r.url || "").toLowerCase();
      return u.includes(`twitter.com/${handle.toLowerCase()}`) || u.includes(`x.com/${handle.toLowerCase()}`);
    });
    if (!tw) return null;

    // 从 description (bio) 中提取 URL
    const text = (tw.description || "") + " " + (tw.title || "");
    const urlMatches = [...text.matchAll(/https?:\/\/[^\s"<>)]+/g)].map(m => m[0]);
    // 过滤掉 Twitter 自身、t.co 短链、blocklist
    const bioUrls = urlMatches.filter(u => {
      try {
        const d = new URL(u).hostname.replace(/^www\./, '').toLowerCase();
        if (["twitter.com", "x.com", "t.co"].some(x => d === x || d.endsWith("." + x))) return false;
        return !isBlockedDomain(u);
      } catch { return false; }
    });
    if (!bioUrls.length) return null;

    // 取第一个非 t.co 短链作为官网
    try {
      const u = new URL(bioUrls[0]);
      return {
        source: "twitter-bio",
        website: `https://${u.hostname}`,
        bioSnippet: text.slice(0, 200),
        score: "high",  // Twitter bio 自报家门，可信度高
      };
    } catch { return null; }
  } catch { return null; }
}

/**
 * 综合自动发现 — 5 路并行探测 + Twitter Bio 二阶段
 * @param {string} projectName
 * @param {object} opts
 * @param {string} opts.proxyBase
 * @param {string} opts.twitterHandle - 已知的 Twitter handle（来自 Claude 或 RootData）
 * @returns {Promise<{website, github, twitter, sources: object, confidence: string}>}
 */
export async function autoDiscoverProject(projectName, { proxyBase = "", twitterHandle = null } = {}) {
  // 阶段1: 并行 4 路（不依赖 Twitter handle）
  const [gh, defi, domain, brave] = await Promise.all([
    discoverViaGitHub(projectName),
    discoverViaDefiLlama(projectName),
    discoverViaDomainPattern(projectName, proxyBase),
    discoverViaBraveSearch(projectName, proxyBase),
  ]);

  // 阶段2: Twitter Bio 抓取（如已知 handle，或从其他源获得）
  const handleForBio = twitterHandle || defi?.twitter || null;
  const twBio = handleForBio ? await discoverViaTwitterBio(handleForBio, proxyBase) : null;

  const discovered = { website: "", github: "", twitter: "", sources: {} };
  // 优先级 (官方/可信度高 → 模糊):
  // Twitter bio (自报) > DeFiLlama (官方注册) > Brave Search 精准 > GitHub homepage > Brave 部分 > 域名猜测
  const websiteCandidates = [
    twBio?.website && twBio.score === "high" ? { url: twBio.website, src: "twitter-bio" } : null,
    defi?.website ? { url: defi.website, src: "defillama" } : null,
    brave?.website && brave.score === "high" ? { url: brave.website, src: "brave-search" } : null,
    gh?.website ? { url: gh.website, src: "github" } : null,
    brave?.website && brave.score === "medium" ? { url: brave.website, src: "brave-search" } : null,
    domain?.website ? { url: domain.website, src: "domain-pattern" } : null,
    brave?.website ? { url: brave.website, src: "brave-search-fallback" } : null,
  ].filter(Boolean);

  if (websiteCandidates.length) {
    discovered.website = websiteCandidates[0].url;
    discovered.sources.website = websiteCandidates[0].src;
  }
  if (gh?.github) { discovered.github = gh.github; discovered.sources.github = "github"; }
  if (defi?.twitter) { discovered.twitter = defi.twitter; discovered.sources.twitter = "defillama"; }

  // 置信度：多源同意 → high, 任一高质量源 → medium, 仅域名猜测 → low
  const highSources = [twBio?.score === "high", defi?.score === "high", brave?.score === "high", gh?.score === "high"].filter(Boolean).length;
  discovered.confidence = highSources >= 2 ? "high"
                        : highSources === 1 ? "medium"
                        : (gh || defi || brave || domain) ? "low"
                        : "none";

  // 调试信息
  discovered._debug = {
    github: gh ? `${gh.website || '?'} (★${gh.stars})` : null,
    defillama: defi?.website || null,
    domain_pattern: domain?.website || null,
    brave_search: brave?.website ? `${brave.website} [${brave.score}]` : null,
    twitter_bio: twBio?.website || null,
  };

  return discovered;
}
